import * as Joi from 'joi';
import { DateTime } from 'luxon';

import {
  EnumAppDocFormat,
  EnumAppDocType,
  EnumAppSignatureType,
  EnumFileStatus,
  EnumHasScholarship,
  EnumHearAboutSurvey,
  EnumProfileRelation,
  EnumSections
} from './scholarship.models';

const JoiCert = Joi.object({
  firstName: Joi.string().trim().required(),
  lastName: Joi.string().trim().required(),
  signed: Joi.boolean().valid(true, false).required(),
  signedAgreementWording: Joi.string().allow(''),
  checked: Joi.boolean().valid(true, false),
  signatureType: Joi.string(),
  signatureFormat: Joi.string().valid('digital', 'paper').required().messages({
    'any.only': 'Signature format must be one of the following values: digital, paper.'
  })
});

const JoiEssayCert = Joi.object({
  firstName: Joi.string().trim().required(),
  lastName: Joi.string().trim().required(),
  signed: Joi.boolean().valid(true).required().messages({
    'any.only': 'Please certify your signature by checking the box.'
  }),
  signedAgreementWording: Joi.string(),
  checked: Joi.boolean().valid(true, false),
  signatureType: Joi.string(),
  signatureFormat: Joi.string().valid('digital', 'paper').required().messages({
    'any.only': 'Signature format must be one of the following values: digital, paper.'
  })
}).options({ stripUnknown: true });

// Validation for all POSTS to publicSave
export const JoiPublicSave = Joi.object({
  data: Joi.object().unknown().required(),
  section: Joi.string()
    .lowercase()
    .valid(...Object.values(EnumSections))
    .required(),
  id: Joi.string().required()
}).options({ stripUnknown: true });

// Common Validations
const JoiSignature = Joi.object({
  firstName: Joi.string().trim().required(),
  lastName: Joi.string().trim().required(),
  signed: Joi.boolean().required(),
  signedAgreementWording: Joi.string(),
  signatureId: Joi.string(),
  signatureType: Joi.string()
    .required()
    .valid(...Object.values(EnumAppSignatureType))
}).options({ stripUnknown: true });

export const JoiPublicAppSaveFinancial = Joi.object({
  income: Joi.number().integer().positive().max(120000).required().messages({
    'any.required': 'Please provide an income.',
    'number.base': 'Income may only be a number.',
    'number.positive': 'Please provide an income greater than 0.',
    'number.max': 'Income may not exceed 120,000.'
  }),
  dependents: Joi.number().integer().positive().min(1).required().messages({
    'any.required': 'Please provide a number of dependents .',
    'number.base': 'Dependents may only be a number.',
    'number.positive': 'Please provide a number of dependents greater than 0.'
  })
}).options({ stripUnknown: true });

export const JoiPublicAppSaveEssay = Joi.object({
  docContent: Joi.string().messages({
    'any.required': 'You must either paste the text of the essay or upload a file.',
    'string.empty': 'You must either paste the text of the essay or upload a file.'
  }),
  filePath: Joi.string(),
  docFormat: Joi.string()
    .required()
    .default(EnumAppDocFormat.TEXT)
    .valid(...Object.values(EnumAppDocFormat)),
  docStatus: Joi.string().valid(...Object.values(EnumFileStatus)),
  docType: EnumAppDocType.ESSAY,
  essaySignature: JoiEssayCert
})
  .xor('docContent', 'filePath')
  .messages({
    'object.xor': 'You must either paste the text of the essay or upload a file.',
    'object.missing': 'You must either paste the text of the essay or upload a file.'
  })
  .options({ stripUnknown: true });

export const JoiPublicAppSaveTranscript = Joi.object({
  docContent: Joi.string(), // will either be the pasted contents of the essay or link to file on s3
  docFormat: Joi.string()
    .required()
    .default(EnumAppDocFormat.TEXT)
    .valid(...Object.values(EnumAppDocFormat)),
  docStatus: Joi.string().valid(...Object.values(EnumFileStatus)),
  docType: EnumAppDocType.TRANSCRIPT,
  filePath: Joi.string()
}).options({ stripUnknown: true });

export const JoiPublicAppSaveProfiles = Joi.object({
  linkedChildProfile: Joi.string().required().messages({
    'any.required': 'Please select a student and parent profile to continue.',
    'string.empty': 'Please select a student and parent profile to continue.'
  }),
  linkedParentProfile: Joi.string().required().messages({
    'any.required': 'Please select a student and parent profile to continue.',
    'string.empty': 'Please select a student and parent profile to continue.'
  }),
  hearAboutSurvey: Joi.string()
    .required()
    .valid(...Object.values(EnumHearAboutSurvey))
    .messages({
      'any.required': 'Please answer "How did you learn about the Match the Promise program?"',
      'string.empty': 'Please answer "How did you learn about the Match the Promise program?"'
    }),
  hearAboutSurveyOther: Joi.when('hearAboutSurvey', {
    is: 'other',
    then: Joi.string().required().trim().messages({
      'any.required': 'Please tell us how you learned about the program.',
      'string.empty': 'Please tell us how you learned about the program.'
    }),
    otherwise: Joi.optional()
  }),
  lastUpdated: Joi.number()
    .integer()
    .required()
    .valid(DateTime.now().setZone('America/Chicago').year)
    .error(new Error(`Please review and update the student's profile.`))
}).options({ stripUnknown: true });

export const JoiPublicAppSaveSignatures = Joi.object({
  id: Joi.string().required(),
  parentSignature: JoiSignature.keys({
    signatureType: Joi.string().required().valid(EnumAppSignatureType.PARENT),
    signed: Joi.boolean().valid(true).messages({
      'any.only': 'Please certify your signature by checking the box.'
    })
  }).required()
}).options({ stripUnknown: true });

export const JoipublicAppSaveSignature = Joi.object({
  data: Joi.array().items(
    Joi.object({
      customEmailBody: Joi.string().allow('').default(''),
      signatureId: Joi.string(),
      signed: Joi.any(),
      signedAgreementWording: Joi.any(),
      signatureType: Joi.string(),
      signatureFormat: Joi.string().valid('digital', 'paper').required().messages({
        'any.only': 'Signature format must be one of the following values: digital, paper.'
      }),
      firstName: Joi.string().required().messages({
        'any.required': 'Please provide a first name.',
        'string.empty': 'Please provide a first name.'
      }),
      lastName: Joi.string().required().messages({
        'any.required': 'Please provide a last name.',
        'string.empty': 'Please provide a last name.'
      }),
      email: Joi.when('signatureFormat', {
        is: 'paper',
        then: Joi.any()
      }).when('signatureFormat', {
        is: 'digital',
        then: Joi.string().email({ tlds: false }).message('Please provide a valid email address.').messages({
          'string.empty': 'Please provide an email address.'
        })
      }),
      docStatus: Joi.string()
        .valid(...Object.values(EnumFileStatus))
        .optional(),
      filePath: Joi.string().optional(),
      userId: Joi.string().optional()
    })
  ),
  id: Joi.string().required(),
  section: Joi.string().valid('signatures').required()
}).options({ stripUnknown: true });

// The TTPF ID is a 10 digit number. Joi does not have a digit count check.
// Workaround is to use a string type and add a regex to ensure the values are numeric.
export const JoiPublicAppSaveTtpf = Joi.object({
  ttpfParentSignature: Joi.when('notParentTtpfAccount', {
    is: true,
    then: JoiCert.required().messages({
      'any.required': 'Please sign the Parent/Guardian Non-Purchaser Certification',
      'string.empty': 'Please sign the Parent/Guardian Non-Purchaser Certification'
    })
  }),
  ttpfPurchaserSignature: Joi.when('notParentTtpfAccount', {
    is: true,
    then: JoiCert.required().messages({
      'any.required': 'A purchaser is required. Please add a Purchaser.',
      'string.empty': 'A purchaser is required. Please add a Purchaser.'
    })
  }),
  notParentTtpfAccount: Joi.boolean().default(false).messages({
    'boolean.base': 'Please select one of the radio buttons below to indicate ownership status.'
  }),
  ttpfId: Joi.string().required().length(10).regex(/^\d+$/).message('The account number may only contain numbers.').messages({
    'any.required': 'Please provide an account number.',
    'string.empty': 'Please provide an account number.',
    'string.length': 'The account number provided must have a length of 10 digits.'
  }),

  ttpfOwnerFirstName: Joi.string().trim().required().messages({
    'any.required': 'Please provide the first name of the Account Owner/Purchaser.',
    'string.empty': 'Please provide the first name of the Account Owner/Purchaser.'
  }),
  ttpfOwnerLastName: Joi.string().trim().required().messages({
    'any.required': 'Please provide the last name of the Account Owner/Purchaser.',
    'string.empty': 'Please provide the last name of the Account Owner/Purchaser.'
  })
}).options({ stripUnknown: true });

// Profile Validations
export const JoiPublicProfileSaveChild = Joi.object({
  firstName: Joi.string().required().trim().messages({
    'any.required': 'Please provide a first name.',
    'string.empty': 'Please provide a first name.'
  }),
  lastName: Joi.string().required().trim().messages({
    'any.required': 'Please provide a last name.',
    'string.empty': 'Please provide a last name.'
  }),
  address1: Joi.string().required().trim().messages({
    'any.required': 'Please provide an address.',
    'string.empty': 'Please provide an address.'
  }),
  address2: Joi.string().allow(null, '').trim(),
  addressCountry: Joi.string().required().max(2).trim().messages({
    'any.required': 'Please provide an address country.',
    'string.empty': 'Please provide an address country.'
  }),
  addressCounty: Joi.string().required().trim().messages({
    'any.required': 'Please provide an address county.',
    'string.empty': 'Please provide an address county.'
  }),
  addressCity: Joi.string().required().trim().messages({
    'any.required': 'Please provide a city.',
    'string.empty': 'Please provide a city.'
  }),
  addressState: Joi.string().required().max(2).min(2).trim().messages({
    'any.required': 'Please provide a 2 character state abbreviation.',
    'string.empty': 'Please provide a 2 character state abbreviation.',
    'string.max': 'Please provide a 2 character state abbreviation.',
    'string.min': 'Please provide a 2 character state abbreviation.'
  }),
  addressZip: Joi.string().required().min(5).max(10).trim().messages({
    'any.required': 'Please provide a zip code with a length between 5 and 10 characters.',
    'string.empty': 'Please provide a zip code with a length between 5 and 10 characters.',
    'string.min': 'Please provide a zip code with a length between 5 and 10 characters.',
    'string.max': 'Please provide a zip code with a length between 5 and 10 characters.'
  }),
  hasScholarship: Joi.string()
    .valid(...Object.values(EnumHasScholarship))
    .required(),
  hasMailingAddress: Joi.boolean().required().default(false),
  mailing1: Joi.when('hasMailingAddress', {
    is: false,
    then: Joi.string()
      .replace(/([\s\S]*)/, '')
      .allow(''),
    otherwise: Joi.string().trim().required().messages({
      'any.required': 'A full mailing address is required if you indicate it is different from your physical address.',
      'string.empty': 'A full mailing address is required if you indicate it is different from your physical address.'
    })
  }),
  mailing2: Joi.when('hasMailingAddress', {
    is: false,
    then: Joi.string()
      .replace(/([\s\S]*)/, '')
      .allow(''),
    otherwise: Joi.string().allow(null, '').trim()
  }),
  mailingCity: Joi.when('hasMailingAddress', {
    is: false,
    then: Joi.string()
      .replace(/([\s\S]*)/, '')
      .allow(''),
    otherwise: Joi.string().trim().required().messages({
      'any.required': 'A full mailing address is required if you indicate it is different from your physical address.',
      'string.empty': 'A full mailing address is required if you indicate it is different from your physical address.'
    })
  }),
  mailingCounty: Joi.string().allow(null, '').trim(),
  mailingCountry: Joi.when('hasMailingAddress', {
    is: false,
    then: Joi.string()
      .replace(/([\s\S]*)/, '')
      .allow(''),
    otherwise: Joi.string().trim().required().messages({
      'any.required': 'A full mailing address is required if you indicate it is different from your physical address.',
      'string.empty': 'A full mailing address is required if you indicate it is different from your physical address.'
    })
  }),
  mailingState: Joi.when('hasMailingAddress', {
    is: false,
    then: Joi.string()
      .replace(/([\s\S]*)/, '')
      .allow(''),
    otherwise: Joi.string().trim().required().messages({
      'any.required': 'A full mailing address is required if you indicate it is different from your physical address.',
      'string.empty': 'A full mailing address is required if you indicate it is different from your physical address.'
    })
  }),
  mailingZip: Joi.when('hasMailingAddress', {
    is: false,
    then: Joi.string()
      .replace(/([\s\S]*)/, '')
      .allow(''),
    otherwise: Joi.string().trim().required().messages({
      'any.required': 'A full mailing address is required if you indicate it is different from your physical address.',
      'string.empty': 'A full mailing address is required if you indicate it is different from your physical address.'
    })
  }),
  dob: Joi.date().required().messages({
    'any.required': 'Please provide a valid date of birth.',
    'string.empty': 'Please provide a valid date of birth.',
    'date.base': 'Please provide a valid date of birth.'
  }),
  grade: Joi.number().integer().min(3).max(9).required().messages({
    'any.required': 'Please select a grade.',
    'number.base': 'Please select a grade.'
  })
}).options({ stripUnknown: true });

export const JoiPublicProfileSaveParent = Joi.object({
  email: Joi.string().required().trim().email({ tlds: false }).message('Email is invalid').messages({
    'string.empty': 'Please provide an email address.'
  }),
  firstName: Joi.string().required().trim().messages({
    'any.required': 'Please provide a first name.',
    'string.empty': 'Please provide a first name.'
  }),
  lastName: Joi.string().required().trim().messages({
    'any.required': 'Please provide a last name.',
    'string.empty': 'Please provide a last name.'
  }),
  otherRelationship: Joi.string().trim().allow(null, ''),
  phone: Joi.string()
    .required()
    .trim()
    .replace(/[^0-9]/gi, '')
    .length(10),
  relationship: Joi.string()
    .valid(...Object.values(EnumProfileRelation))
    .required()
    .messages({
      'any.required': 'Please select a relation: father, mother, or legal guardian.',
      'any.only': 'Please select a relation: father, mother, or legal guardian.'
    }),
  address1: Joi.string().required().trim().messages({
    'any.required': 'Please provide an address.',
    'string.empty': 'Please provide an address.'
  }),
  address2: Joi.string().allow(null, '').trim(),
  addressCountry: Joi.string().required().max(2).trim().uppercase(),
  addressCity: Joi.string().required().trim().messages({
    'any.required': 'Please provide a city.',
    'string.empty': 'Please provide a city.'
  }),
  addressCounty: Joi.string().required().trim().messages({
    'any.required': 'Please provide a county.',
    'string.empty': 'Please provide a county.'
  }),
  addressState: Joi.string().required().max(2).trim().messages({
    'any.required': 'Please provide a 2 character state abbreviation.',
    'string.empty': 'Please provide a 2 character state abbreviation.',
    'string.max': 'Please provide a 2 character state abbreviation.'
  }),
  addressZip: Joi.string().required().min(5).max(10).trim().messages({
    'any.required': 'Please provide a zip code with a length between 5 and 10 characters.',
    'string.empty': 'Please provide a zip code with a length between 5 and 10 characters.',
    'string.min': 'Please provide a zip code with a length between 5 and 10 characters.',
    'string.max': 'Please provide a zip code with a length between 5 and 10 characters.'
  }),
  hasMailingAddress: Joi.boolean().required().default(false),
  mailing1: Joi.when('hasMailingAddress', {
    is: false,
    then: Joi.string()
      .replace(/([\s\S]*)/, '')
      .allow(''),
    otherwise: Joi.string().trim().required().messages({
      'any.required': 'A full mailing address is required if you indicate it is different from your physical address.',
      'string.empty': 'A full mailing address is required if you indicate it is different from your physical address.'
    })
  }),
  mailing2: Joi.when('hasMailingAddress', {
    is: false,
    then: Joi.string()
      .replace(/([\s\S]*)/, '')
      .allow(''),
    otherwise: Joi.string().allow(null, '').trim()
  }),
  mailingCity: Joi.when('hasMailingAddress', {
    is: false,
    then: Joi.string()
      .replace(/([\s\S]*)/, '')
      .allow(''),
    otherwise: Joi.string().trim().required().messages({
      'any.required': 'A full mailing address is required if you indicate it is different from your physical address.',
      'string.empty': 'A full mailing address is required if you indicate it is different from your physical address.'
    })
  }),
  mailingCounty: Joi.string().allow(null, '').trim(),
  mailingCountry: Joi.when('hasMailingAddress', {
    is: false,
    then: Joi.string()
      .replace(/([\s\S]*)/, '')
      .allow(''),
    otherwise: Joi.string().trim().required().messages({
      'any.required': 'A full mailing address is required if you indicate it is different from your physical address.',
      'string.empty': 'A full mailing address is required if you indicate it is different from your physical address.'
    })
  }),
  mailingState: Joi.when('hasMailingAddress', {
    is: false,
    then: Joi.string()
      .replace(/([\s\S]*)/, '')
      .allow(''),
    otherwise: Joi.string().trim().required().messages({
      'any.required': 'A full mailing address is required if you indicate it is different from your physical address.',
      'string.empty': 'A full mailing address is required if you indicate it is different from your physical address.'
    })
  }),
  mailingZip: Joi.when('hasMailingAddress', {
    is: false,
    then: Joi.string()
      .replace(/([\s\S]*)/, '')
      .allow(''),
    otherwise: Joi.string().trim().required().messages({
      'any.required': 'A full mailing address is required if you indicate it is different from your physical address.',
      'string.empty': 'A full mailing address is required if you indicate it is different from your physical address.'
    })
  })
}).options({ stripUnknown: true });

export const JoiAddPurchaser = Joi.object({
  ttpfId: Joi.string()
    .required()
    .length(10)
    .regex(/^[0-9]+$/)
    .messages({
      'string.empty': 'Please provide an account number.',
      'string.length': 'The account number provided must have a length of 10 digits.',
      'string.pattern.base': 'The account number may only contain numbers.'
    })
});
